/**
 * OCR scheduler (M5 spec §3.1): turns the sync engine's `pushed` events into
 * debounced page-snapshot uploads.
 *
 * Every pageId in a `pushed` set (re)arms a per-page 30s timer — further
 * pushes for the same page reset it, so a burst of edits yields ONE OCR after
 * the burst settles. On fire: load the page (skip deleted/missing), render
 * the snapshot (ink/snapshot.ts), POST via the engine's postOcr.
 *
 * Deliberately in-memory and lossy: any error (offline, 4xx/5xx, snapshot
 * failure) DROPS the attempt — the next edit of that page re-schedules, and
 * the server's rev-skip makes eventual repeats free. A `skipped: 'quota'`
 * response stops ALL posting until the next UTC month (the server's quota
 * key is the UTC month, see handleOcr).
 *
 * Framework-free: the UI layer owns the lifecycle (start/stop alongside the
 * sync engine).
 */
import type { Page, TextBox } from '../store/types';
import type { Stroke } from '../ink/stroke';
import { renderPageSnapshot } from '../ink/snapshot';
import type { OcrResponse } from './sync';

/** The slice of Repo the scheduler depends on (structural, so tests can stub it). */
export interface OcrRepo {
  getPage(id: string): Promise<Page | undefined>;
  getActiveStrokes(pageId: string): Promise<Stroke[]>;
  getActiveTextBoxes(pageId: string): Promise<TextBox[]>;
}

/** The POST seam — production passes `{ post: (…args) => syncEngine.postOcr(…args) }`. */
export interface OcrPoster {
  post(pageId: string, rev: number, scale: number, png: Blob): Promise<OcrResponse>;
}

/** The slice of SyncEngine the scheduler subscribes to. */
export interface PushedSource {
  on(ev: 'pushed', cb: (pageIds: Set<string>) => void): void;
  off(ev: 'pushed', cb: (pageIds: Set<string>) => void): void;
}

export interface OcrSchedulerOpts {
  /** Per-page settle window. Default 30s; `?ocrDebounce=` override in the UI layer. */
  debounceMs?: number;
  /** Test seam: replaces the real canvas snapshot renderer. */
  snapshot?: (page: Page, strokes: Stroke[], textBoxes: TextBox[]) => Promise<{ blob: Blob; scale: number }>;
}

const DEFAULT_DEBOUNCE_MS = 30_000;

export class OcrScheduler {
  private readonly debounceMs: number;
  private readonly snapshot: NonNullable<OcrSchedulerOpts['snapshot']>;
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Quota month-stop: fires before this UTC ms are dropped (0 = no stop). */
  private resumeAfter = 0;
  private source: PushedSource | null = null;
  private handler: ((pageIds: Set<string>) => void) | null = null;

  constructor(
    private readonly repo: OcrRepo,
    private readonly poster: OcrPoster,
    opts: OcrSchedulerOpts = {},
  ) {
    this.debounceMs = opts.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    this.snapshot = opts.snapshot ?? ((page, strokes, boxes) => renderPageSnapshot(page, strokes, boxes));
  }

  /** Subscribe to the engine's pushed events. Idempotent while started. */
  start(source: PushedSource): void {
    if (this.source) return;
    this.source = source;
    this.handler = (pageIds) => this.schedule(pageIds);
    source.on('pushed', this.handler);
  }

  /** Unsubscribe and drop all pending timers (missed OCRs self-heal on next edit). */
  stop(): void {
    if (this.source && this.handler) this.source.off('pushed', this.handler);
    this.source = null;
    this.handler = null;
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
  }

  private schedule(pageIds: Set<string>): void {
    for (const pageId of pageIds) {
      const existing = this.timers.get(pageId);
      if (existing !== undefined) clearTimeout(existing);
      this.timers.set(
        pageId,
        setTimeout(() => {
          this.timers.delete(pageId);
          void this.fire(pageId);
        }, this.debounceMs),
      );
    }
  }

  private async fire(pageId: string): Promise<void> {
    if (Date.now() < this.resumeAfter) return; // quota month-stop
    try {
      const page = await this.repo.getPage(pageId);
      if (!page || page.deletedAt) return; // since-deleted (or purged) page
      const strokes = await this.repo.getActiveStrokes(pageId);
      const boxes = await this.repo.getActiveTextBoxes(pageId);
      const { blob, scale } = await this.snapshot(page, strokes, boxes);
      const res = await this.poster.post(page.id, page.rev, scale, blob);
      if ('skipped' in res && res.skipped === 'quota') {
        const now = new Date();
        this.resumeAfter = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
      }
      // skipped: 'rev' is a plain no-op — the server already has this rev.
    } catch {
      // Drop on ANY error (offline, sync disabled, snapshot/post failure):
      // the next edit re-schedules, and rev-skip makes repeats idempotent.
    }
  }
}
